import { logger } from './logger';
/**
 * Attention Notification utility
 * Used to bring user attention back to the app when system audio needs attention
 */

let originalTitle = '';
let titleFlashInterval: NodeJS.Timeout | null = null;

/**
 * Request notification permission if not already granted
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
        return false;
    }

    if (Notification.permission === 'granted') {
        return true;
    }

    if (Notification.permission === 'denied') {
        return false;
    }

    try {
        const permission = await Notification.requestPermission();
        return permission === 'granted';
    } catch {
        return false;
    }
};

/**
 * Send a browser notification
 */
export const sendBrowserNotification = (title: string, body: string): Notification | null => {
    logger.log('[Notizy Debug] sendBrowserNotification called');
    logger.log('[Notizy Debug] Notification API available:', 'Notification' in window);
    logger.log('[Notizy Debug] Notification.permission:', ('Notification' in window) ? Notification.permission : 'N/A');

    if (!('Notification' in window)) {
        logger.log('[Notizy Debug] Notification API not available');
        return null;
    }

    if (Notification.permission !== 'granted') {
        logger.log('[Notizy Debug] Notification permission not granted');
        return null;
    }

    try {
        const notification = new Notification(title, {
            body,
            icon: '/icons/icon-192.png',
            tag: 'notizy-attention',
            requireInteraction: true
        });
        logger.log('[Notizy Debug] Notification created successfully');
        return notification;
    } catch (err) {
        logger.error('[Notizy Debug] Failed to create notification:', err);
        return null;
    }
};

/**
 * Start flashing the page title to attract attention
 */
export const startTitleFlash = (alertMessage: string = '⚠️ Aandacht nodig'): void => {
    if (titleFlashInterval) {
        return; // Already flashing
    }

    originalTitle = document.title;
    let isAlternate = false;

    titleFlashInterval = setInterval(() => {
        document.title = isAlternate ? originalTitle : alertMessage;
        isAlternate = !isAlternate;
    }, 1000);
};

/**
 * Stop flashing the page title
 */
export const stopTitleFlash = (): void => {
    if (titleFlashInterval) {
        clearInterval(titleFlashInterval);
        titleFlashInterval = null;
    }
    if (originalTitle) {
        document.title = originalTitle;
        originalTitle = '';
    }
};

/**
 * Play a notification sound
 */
export const playNotificationSound = (): void => {
    try {
        // Create a simple beep using Web Audio API
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        // Create oscillator for a gentle notification sound
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Configure a pleasant notification tone
        oscillator.frequency.value = 880; // A5 note
        oscillator.type = 'sine';

        // Envelope for smooth sound
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
        gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);

        // Play second beep after short pause
        setTimeout(() => {
            const osc2 = audioContext.createOscillator();
            const gain2 = audioContext.createGain();

            osc2.connect(gain2);
            gain2.connect(audioContext.destination);

            osc2.frequency.value = 1100; // C#6 note
            osc2.type = 'sine';

            gain2.gain.setValueAtTime(0, audioContext.currentTime);
            gain2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
            gain2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);

            osc2.start(audioContext.currentTime);
            osc2.stop(audioContext.currentTime + 0.3);
        }, 200);
    } catch (error) {
        logger.warn('Could not play notification sound:', error);
    }
};

/**
 * Check if an audio stream has active audio playing
 * Returns true if audio level is above threshold
 */
export const detectActiveAudio = async (stream: MediaStream, durationMs: number = 500): Promise<boolean> => {
    return new Promise((resolve) => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);

            source.connect(analyser);
            analyser.fftSize = 256;

            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const startTime = Date.now();
            const threshold = 5; // Minimum audio level to consider "active"

            const checkAudio = () => {
                analyser.getByteFrequencyData(dataArray);

                // Calculate average level
                const average = dataArray.reduce((a, b) => a + b, 0) / bufferLength;

                if (average > threshold) {
                    source.disconnect();
                    audioContext.close();
                    resolve(true);
                    return;
                }

                if (Date.now() - startTime < durationMs) {
                    requestAnimationFrame(checkAudio);
                } else {
                    source.disconnect();
                    audioContext.close();
                    resolve(false);
                }
            };

            checkAudio();
        } catch {
            resolve(false);
        }
    });
};

/**
 * Trigger all attention-grabbing mechanisms
 */
export const triggerAttention = async (message: string): Promise<void> => {
    // Play sound immediately
    playNotificationSound();

    // Start title flash
    startTitleFlash('⚠️ ' + message);

    // Send browser notification if permission is granted
    sendBrowserNotification('Notizy', message);

    // Auto-stop title flash when window gets focus
    const handleFocus = () => {
        stopTitleFlash();
        window.removeEventListener('focus', handleFocus);
    };
    window.addEventListener('focus', handleFocus);
};
